import { seoLinks, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import { GuideLinks } from "../components/site/Guides";
import { KnowledgeHubNote } from "../components/site/KnowledgeHubNote";

const title = "Guider för familjer som flyttar till Costa del Sol";
const description =
  "Samlade guider om skolval, svensk förskola, flytten till Spanien, NIE och livet i Cancelada och Estepona – från en svensk skola på plats.";

export const Route = createFileRoute("/guider/")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/guider"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guider"
        title="Guider för familjen"
        intro="Vi får samma frågor om och om igen från familjer som funderar på att flytta till Costa del Sol. Här har vi samlat svaren i längre guider – skrivna av oss som möter familjerna varje dag, utan löften vi inte kan hålla."
      />

      <Prose>
        <h2>Varför vi skriver guiderna själva</h2>
        <p>
          Vi möter varje vecka familjer som står inför en flytt till Costa del Sol. Frågorna
          liknar varandra: när ska vi flytta i förhållande till läsåret, vilken årskurs hamnar
          barnet i, hur fungerar svensk läroplan utomlands och vad behöver ordnas rent praktiskt
          med NIE, folkbokföring och sjukvård. Guiderna nedan är våra samlade svar, skrivna
          utifrån det vi faktiskt ser fungera – inte utifrån löften vi inte kan hålla.
        </p>
        <p>
          Varje guide går att läsa fristående, men de hänger ihop. Börjar ni med tidsplanen för
          flytten är det naturligt att gå vidare till skolvalet, och därefter till det praktiska
          kring bosättningen. Vi uppdaterar texterna när rutiner, terminer eller regler ändras,
          och vi skriver hellre &quot;kontakta oss&quot; än gissar när något beror på er situation.
        </p>

        <h2>Så kan ni läsa dem</h2>
        <ul>
          <li>
            <strong>Planerar ni flytten:</strong> börja med guiden om att flytta till Spanien med
            barn och fortsätt med områdesguiden till Cancelada och Estepona.
          </li>
          <li>
            <strong>Jämför ni skolor:</strong> läs om att välja skola utomlands och hur svensk
            läroplan fungerar i Spanien.
          </li>
          <li>
            <strong>Har ni äldre barn:</strong> guiden om distansstudier beskriver hur högstadiet
            och gymnasiet fungerar hos oss.
          </li>
          <li>
            <strong>Är flytten nära:</strong> guiden om NIE och folkbokföring täcker de ärenden
            som brukar ta längst tid.
          </li>
        </ul>
        <h2>Guider eller kunskapsbank?</h2>
        <p>
          Guiderna här är öppna översikter: de förklarar hur saker hänger ihop och svarar på de
          frågor vi får oftast. Kunskapsbanken är fördjupningen för er som redan bestämt er –
          checklistor månad för månad, ordningen mellan myndighetsärendena, vad vardagen kostar
          och vilka nummer som bör finnas i telefonen. Materialet överlappar alltså inte
          guiderna, utan tar vid där de slutar.
        </p>
        <p>
          Saknar ni något svar är ni alltid välkomna att höra av er till{" "}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">
            info@sweintschool.com
          </a>{" "}
          – frågor vi får ofta blir förr eller senare en ny guide.
        </p>
      </Prose>

      <KnowledgeHubNote resource="flyttchecklistan" />
      <GuideLinks title="Alla guider" />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
